import { send } from './client';

/**
 * The administrator endpoints.
 *
 * A separate module from `client.ts` and `mfa.ts` for the same reason those
 * two are separate: a different audience and a different lifetime. Everything
 * here needs `user.*` permissions and acts on somebody ELSE's account, and
 * mixing it into the session client would put "sign me in" and "reset that
 * person's password" in one object.
 *
 * Every call goes through `send`, so it inherits the CSRF handshake, the
 * one-shot 419 retry, `credentials: 'include'` and the single `ApiError`
 * envelope. There is no second fetch path in this application.
 */

export interface Role {
  uuid: string;
  name: string;
  label_en: string;
  label_ar: string;
  /** Assigning this role imposes two-step verification on the account. */
  mfa_required: boolean;
  /**
   * One of the five roles the application depends on. Its permissions may be
   * changed; it may never be renamed or deleted.
   */
  built_in: boolean;
  /** `administrator`, and nothing else. Cannot be changed at all. */
  protected: boolean;
  /**
   * An administrator has taken this role's permissions over, and the seeder
   * no longer manages it — so a permission added by a future release will not
   * reach it on its own.
   */
  permissions_customised: boolean;
  /**
   * ABSENT unless the role was loaded for management. `GET /roles` is read by
   * the user form too, whose caller may only ASSIGN roles and has no use for
   * the matrix — so these are undefined there rather than empty.
   */
  holders?: number;
  permissions?: readonly string[];
}

/** One capability a role may hold. `GET /permissions`. */
export interface Permission {
  name: string;
  description: string | null;
  /**
   * Administrator-only: it cannot be granted to another role. Returned rather
   * than filtered out so the screen can show it disabled WITH A REASON — an
   * administrator wondering why they cannot grant `user.create` should find
   * the answer on the page, not conclude the feature is broken.
   */
  reserved: boolean;
}

export interface RoleInput {
  name: string;
  label_en: string;
  label_ar: string;
  mfa_required?: boolean;
  permissions: readonly string[];
}

/** `AdminUserResource`'s `dispensary` key — the named form of `dispensary_id`. */
export interface AdminUserDispensary {
  uuid: string;
  name_en: string;
  name_ar: string;
}

export interface AdminUser {
  uuid: string;
  username: string;
  full_name: string;
  email: string;
  organisation: string | null;
  /**
   * THE INTERNAL INTEGER. Kept because the API still publishes it, but it
   * resolves to nothing a client can fetch: `GET /reference/dispensaries`
   * answers uuids. Read `dispensary` below instead — this exists so an older
   * response still parses.
   */
  dispensary_id: number | null;
  /** `null` for an unscoped account: an administrator, a call-centre agent. */
  dispensary: AdminUserDispensary | null;
  locale: string;
  is_active: boolean;
  must_change_password: boolean;
  can_view_escalations: boolean;
  mfa_required: boolean;
  mfa_enabled: boolean;
  last_login_at: string | null;
  roles: readonly Role[];
}

/** What `POST /users` returns: the account, and the one-time password. */
export interface CreatedUser {
  user: AdminUser;
  temporary_password: string;
}

export interface UserInput {
  username: string;
  full_name: string;
  email: string;
  locale: string;
  organisation: string | null;
  /**
   * THE DISPENSARY'S UUID, or null to unscope the account. Sent instead of
   * `dispensary_id`: the picker is fed by `GET /reference/dispensaries`,
   * which never publishes the integer key, so the uuid is the only value the
   * screen actually has. The server accepts either and prefers this.
   */
  dispensary: string | null;
  can_view_escalations: boolean;
  mfa_required: boolean;
}

export const usersApi = {
  list: () => send<readonly AdminUser[]>('/users'),

  get: (uuid: string) => send<AdminUser>(`/users/${uuid}`),

  create: (input: UserInput) => send<CreatedUser>('/users', { method: 'POST', body: input }),

  update: (uuid: string, input: Partial<UserInput>) =>
    send<AdminUser>(`/users/${uuid}`, { method: 'PATCH', body: input }),

  /** REPLACES the set. An empty array is how a role is removed. */
  assignRoles: (uuid: string, roles: readonly string[]) =>
    send<AdminUser>(`/users/${uuid}/roles`, { method: 'PUT', body: { roles } }),

  activate: (uuid: string) => send<AdminUser>(`/users/${uuid}/activate`, { method: 'POST' }),

  deactivate: (uuid: string) => send<AdminUser>(`/users/${uuid}/deactivate`, { method: 'POST' }),

  /**
   * The temporary password is readable ONCE, here. Never log this result.
   *
   * `password` OPTIONAL. Omitted, the server generates a 20-character one —
   * the only behaviour this function had before, and still what an empty
   * body means. Supplied, the server sets that value instead (minimum
   * twelve characters, `422` with `details.password` below that).
   *
   * NO BODY AT ALL when nothing was chosen, rather than `{ password:
   * undefined }`. `AdminPasswordResetRequest` declares
   * `additionalProperties: false` and the rule is `sometimes` — a key that
   * is present-but-empty is a validation failure, not a request to
   * generate. `JSON.stringify` would drop an `undefined` value anyway, but
   * relying on that is relying on a serialiser detail to carry a contract.
   */
  resetPassword: (uuid: string, password?: string) =>
    send<{ temporary_password: string }>(`/users/${uuid}/password-reset`, {
      method: 'POST',
      ...(password === undefined ? {} : { body: { password } }),
    }),

  resetMfa: (uuid: string) => send<null>(`/users/${uuid}/mfa`, { method: 'DELETE' }),
};

export const rolesApi = {
  /**
   * The catalogue an assignment picks from. Needs `user.role.assign`.
   *
   * The SAME endpoint the management screen reads — `permissions` and
   * `holders` simply arrive undefined for a caller who only holds
   * `user.role.assign`, because the server omits them rather than emptying
   * them.
   */
  list: () => send<readonly Role[]>('/roles'),

  /** All four below need `role.manage`, which only `administrator` holds. */
  create: (input: RoleInput) => send<Role>('/roles', { method: 'POST', body: input }),

  update: (uuid: string, input: Partial<Omit<RoleInput, 'name' | 'permissions'>>) =>
    send<Role>(`/roles/${uuid}`, { method: 'PATCH', body: input }),

  /**
   * The FULL set, replacing what is there — not add/remove calls, which leave
   * a moment where the role holds neither set.
   *
   * MARKS THE ROLE CUSTOMISED: the seeder stops managing it from here on.
   */
  setPermissions: (uuid: string, permissions: readonly string[]) =>
    send<Role>(`/roles/${uuid}/permissions`, { method: 'PUT', body: { permissions } }),

  /** 409 while any account still holds it. */
  remove: (uuid: string) => send<void>(`/roles/${uuid}`, { method: 'DELETE' }),
};

export const permissionsApi = {
  list: () => send<readonly Permission[]>('/permissions'),
};
